
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  /** Optional callback to submit a prayer */
  onSubmitPrayer?: () => void;
  /** Optional callback to refresh/check again */
  onRefresh?: () => void;
}

/**
 * EmptyState Component
 * Displayed when no prayers are available to view
 * Shows a peaceful illustration with helpful message
 */
export function EmptyState({ onSubmitPrayer, onRefresh }: EmptyStateProps) {
  return (
    <div 
      className={cn(
        "bg-white dark:bg-slate-800 rounded-2xl shadow-xl",
        "p-8 sm:p-12 max-w-md w-full mx-auto text-center",
        "animate-fade-in"
      )}
      role="status"
      aria-label="No prayers available"
    >
      {/* Peaceful illustration */}
      <div className="mb-6">
        <div 
          className={cn(
            "w-24 h-24 mx-auto rounded-full",
            "bg-gradient-to-br from-primary-100 to-primary-200",
            "dark:from-primary-900/30 dark:to-primary-800/30",
            "flex items-center justify-center"
          )}
        >
          <svg 
            className="w-12 h-12 text-primary-600 dark:text-primary-400"
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </div>
      </div>

      {/* Message */}
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">
        All Caught Up
      </h2>
      <p className="text-slate-600 dark:text-slate-400 mb-8">
        No prayers at this time. Check back soon or submit your own prayer request.
      </p>

      {/* Action buttons */}
      <div className="space-y-3">
        {onRefresh && (
          <button
            onClick={onRefresh}
            className={cn(
              "w-full py-3 px-6",
              "bg-primary-600 hover:bg-primary-700 active:bg-primary-800",
              "text-white font-semibold rounded-xl",
              "transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2",
              "dark:focus:ring-offset-slate-800"
            )}
          >
            Check Again
          </button>
        )}
        {onSubmitPrayer && (
          <button
            onClick={onSubmitPrayer}
            className={cn(
              "w-full py-3 px-6",
              "bg-slate-100 hover:bg-slate-200 active:bg-slate-300",
              "dark:bg-slate-700 dark:hover:bg-slate-600 dark:active:bg-slate-500",
              "text-slate-700 dark:text-slate-200 font-semibold rounded-xl",
              "transition-all duration-200",
              "focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2",
              "dark:focus:ring-offset-slate-800"
            )}
          >
            Submit a Prayer
          </button>
        )}
      </div>

      {/* Encouraging message */}
      <p className="mt-8 text-sm text-slate-500 dark:text-slate-500 italic">
        "Pray without ceasing." — 1 Thessalonians 5:17
      </p>
    </div>
  );
}

export default EmptyState;
